import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { Page } from '@/api/types';
import { Button } from '@/components/Button';
import { WarehouseSheet } from './WarehouseSheet';
import { ConfirmWindow, WRITES_ENABLED } from '@/components/MaintenanceForm';
import { removeWarehouse, defaultInboundWarehouse, reorderWarehouses } from './api';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { dateTime } from '@/lib/format';
import { Chips, Segmented, DataTable, DeskHead, Group, Cell, PhoneBar, ReadonlyNote, type Col } from './kit/Kit';
import { useApi, useUrlState } from './kit/hooks';
import { activeState, carrierType } from './status';
import type { AssemblyLine, AssemblyLineActiveCount, AssemblyWorker, CarrierCompany, Shop, ShopsPayload, Warehouse } from './types';
import { AssemblyLineDeleteDialog, AssemblyLineSheet, AssemblyWorkerSheet } from './AssemblyLineSheet';
import { updateAssemblyLine } from './api';
import { CarrierSheet } from './CarrierSheet';
import { CarrierImportDialog } from './CarrierImportDialog';
import { ShopDeleteDialog, ShopSheet } from './ShopSheet';
import { reorderCarriers, setDefaultShop } from './api';
import s from './ResourcePages.module.css';

/** 设置子页的外框：手机是二级页（返回「系统设置」，Tab 栏由壳层隐藏）；桌面 / iPad 是设置侧栏右边的主区 */
function Frame({ title, sub, legacy, mobileFooter, children }: { title: string; sub?: ReactNode; legacy?: ReactNode; mobileFooter?: ReactNode; children: ReactNode }) {
  const ff = useFormFactor();
  if (ff === 'phone') {
    return (
      <div className={s.phone}>
        <PhoneBar back="/settings" backLabel="设置" title={title} sub={sub} right={legacy} />
        <div className={s.pscroll}>{children}{import.meta.env.VITE_READONLY !== '0' && <ReadonlyNote />}</div>
        {mobileFooter && <footer className={s.mobileFooter}>{mobileFooter}</footer>}
      </div>
    );
  }
  return (
    <div className={`${s.desk} ${ff === 'tablet' ? s.tablet : ''}`}>
      <DeskHead title={title} sub={sub} className={s.resourceWideHead}>{legacy}</DeskHead>
      <div className={s.scroll}>{children}{import.meta.env.VITE_READONLY !== '0' && <ReadonlyNote />}</div>
    </div>
  );
}

function Loading() {
  return <div className={s.pad}><Skeleton /><Skeleton lines={3} /></div>;
}

/* ================================ 组装线与工人（Y2） ================================ */

export function AssemblyPage() {
  const can = useSession((st) => st.can);
  const writable = can('system.settings.write');
  const [sheet, setSheet] = useState<'line' | 'worker' | null>(null);
  const [editingLine, setEditingLine] = useState<AssemblyLine | null>(null);
  const [deletingLine, setDeletingLine] = useState<AssemblyLine | null>(null);
  const [lineAction, setLineAction] = useState<string | null>(null);
  const [lineActionError, setLineActionError] = useState('');
  // 停用的线和工人只有能改设置的人看得到（老接口 includeInactive=1 要 system.settings.write）
  const q = writable ? { includeInactive: 1 } : undefined;
  const lines = useApi<AssemblyLine[]>('/api/assembly-lines', q);
  const workers = useApi<AssemblyWorker[]>('/api/assembly-workers', q);
  const activeCounts = useApi<AssemblyLineActiveCount[]>('/api/v2/assembly-lines/active-counts');
  const legacy = writable ? <span className={s.headerActions}><button type="button" onClick={() => setSheet('worker')}>新增工人</button><button type="button" className={s.headerPrimary} onClick={() => setSheet('line')}>新增组装线</button></span> : undefined;

  const list = lines.data || [];
  const assemblingByLine = new Map((activeCounts.data || []).map((row) => [row.assemblyLineId, row.assemblingCount]));
  const unassigned = (workers.data || []).filter((w) => !w.assemblyLineId);
  const workerCount = new Set([...(workers.data || []).map((w) => w.id), ...list.flatMap((l) => l.workers.map((w) => w.id))]).size;
  const sub = lines.data ? `${list.filter((l) => l.isActive).length} 条线在用 · ${workerCount} 个工人` : undefined;
  const toggleLine = async (line: AssemblyLine) => {
    if (import.meta.env.VITE_READONLY !== '0' || lineAction) return;
    setLineAction(line.id);
    setLineActionError('');
    try {
      await updateAssemblyLine(line.id, {
        name: line.name,
        sortOrder: line.sortOrder,
        isActive: !line.isActive,
        workerIds: line.workers.map((worker) => worker.id),
        expectedWorkerIds: line.workers.map((worker) => worker.id),
        expectedUpdatedAt: line.updatedAt,
      });
      lines.reload();
      workers.reload();
    } catch (cause) {
      setLineActionError(cause instanceof Error ? cause.message : '组装线状态更新失败，请刷新后重试。');
    } finally {
      setLineAction(null);
    }
  };

  let body: ReactNode;
  if (lines.error) body = <ErrorState title="组装线加载失败" message={lines.error.message} detail="GET /api/assembly-lines" onRetry={lines.reload} />;
  else if (lines.loading && !lines.data) body = <Loading />;
  else if (!list.length) body = <Empty icon="merge" title="还没有组装线">订单开始组装时要选组装线。</Empty>;
  else
    body = (
      <>
        <p className={s.intro}>订单开始组装时选组装线。组装线名称和工人名单会<b>存快照</b>，之后改名、换人不影响历史订单。</p>
        {lineActionError && <p className={s.lineActionError} role="alert">{lineActionError}</p>}
        <div className={s.cards}>
          {list.map((l) => {
            const st = l.isActive ? { label: '启用', tone: 'green' as const } : { label: '已停用', tone: 'none' as const };
            return (
              <section key={l.id} className={`${s.card} ${l.isActive ? '' : s.off}`}>
                <header>
                  <b>{l.name}</b>
                  <Tag tone={st.tone} sm>{st.label}</Tag>
                  <span className={s.meta}><b className="tnum">{activeCounts.data ? assemblingByLine.get(l.id) ?? 0 : '—'}</b> 张在组装</span>
                </header>
                <div className={s.chips}>
                  {l.workers.length ? l.workers.map((w) => <span key={w.id} className={w.isActive ? s.wk : `${s.wk} ${s.wkOff}`}>{w.name}{!w.isActive && <i>停用</i>}</span>) : <span className="em">还没有工人</span>}
                </div>
                {!l.isActive && <p className={s.cardNote}>停用的线不出现在「开始组装」的下拉里，历史订单的快照名照常显示。</p>}
                {writable && <div className={s.lineControls}>
                  <button type="button" disabled={lineAction !== null || import.meta.env.VITE_READONLY !== '0'} onClick={() => void toggleLine(l)}>{lineAction === l.id ? '处理中…' : l.isActive ? '停用' : '启用'}</button>
                  <button type="button" disabled={lineAction !== null} onClick={() => setEditingLine(l)}>编辑</button>
                  {!l.isActive && <button type="button" disabled={lineAction !== null} onClick={() => setDeletingLine(l)}>删除</button>}
                </div>}
              </section>
            );
          })}
          {unassigned.length > 0 && (
            <section className={`${s.card} ${s.dashed}`}>
              <header><b>未分配组装线的工人</b><span className={s.meta}>{unassigned.length} 人</span></header>
              <div className={s.chips}>
                {unassigned.map((w) => <span key={w.id} className={w.isActive ? s.wk : `${s.wk} ${s.wkOff}`}>{w.name}{!w.isActive && <i>停用</i>}</span>)}
              </div>
            </section>
          )}
        </div>
        {!writable && <p className={s.foot}>只显示启用中的组装线和工人。</p>}
      </>
    );
  return <Frame title="组装线与工人" sub={sub} legacy={legacy}>{body}
    {sheet === 'line' && <AssemblyLineSheet lines={list} workers={workers.data || []} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); lines.reload(); workers.reload(); }} />}
    {editingLine && <AssemblyLineSheet key={editingLine.id} line={editingLine} lines={list} workers={workers.data || []} onClose={() => setEditingLine(null)} onSaved={() => { setEditingLine(null); lines.reload(); workers.reload(); }} />}
    {deletingLine && <AssemblyLineDeleteDialog line={deletingLine} onClose={() => setDeletingLine(null)} onDeleted={() => { setDeletingLine(null); lines.reload(); workers.reload(); }} />}
    {sheet === 'worker' && <AssemblyWorkerSheet lines={list} onClose={() => setSheet(null)} onSaved={() => { setSheet(null); lines.reload(); workers.reload(); }} />}
  </Frame>;
}

/* ================================ 物流公司（Y3） ================================ */

export function CarriersPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const writable = can('system.logistics.write');
  const [sp, set] = useUrlState();
  const [editing, setEditing] = useState<CarrierCompany | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [sorting, setSorting] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const dragId = useRef<string | null>(null);
  const type = sp.get('type') || 'all';
  const res = useApi<CarrierCompany[]>('/api/v2/carriers');
  const all = res.data || [];
  const nExp = all.filter((c) => c.type === 'EXPRESS').length;
  const nLog = all.filter((c) => c.type === 'LOGISTICS').length;
  const rows = type === 'all' ? all : all.filter((c) => c.type === type);
  const orderedRows = sorting ? [...rows].sort((a, b) => draftIds.indexOf(a.id) - draftIds.indexOf(b.id)) : rows;
  // 序号 = 发货下拉总顺序；拖动仍限制在同类型内（接口按 类型 → sortOrder 返回）
  const order = new Map<string, number>();
  all.forEach((carrier, index) => {
    order.set(carrier.id, index + 1);
  });
  const move = async (carrier: CarrierCompany, direction: -1 | 1) => {
    if (!writable || moving || import.meta.env.VITE_READONLY !== '0') return;
    const sameType = all.filter((item) => item.type === carrier.type);
    const index = sameType.findIndex((item) => item.id === carrier.id);
    const target = index + direction;
    if (target < 0 || target >= sameType.length) return;
    const ids = sameType.map((item) => item.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setMoving(true); setMoveError('');
    try { await reorderCarriers({ type: carrier.type, ids, expected: sameType.map(({ id, updatedAt }) => ({ id, updatedAt })) }); res.reload(); }
    catch (cause) { setMoveError(cause instanceof Error ? cause.message : '排序失败'); }
    finally { setMoving(false); }
  };
  const moveTo = async (sourceId: string, targetId: string) => {
    if (!writable || moving || import.meta.env.VITE_READONLY !== '0' || sourceId === targetId) return;
    const source = all.find((item) => item.id === sourceId);
    const target = all.find((item) => item.id === targetId);
    if (!source || !target || source.type !== target.type) return;
    const sameType = all.filter((item) => item.type === source.type);
    const from = sameType.findIndex((item) => item.id === sourceId);
    const to = sameType.findIndex((item) => item.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const ids = sameType.map((item) => item.id);
    ids.splice(from, 1);
    ids.splice(to, 0, sourceId);
    setMoving(true); setMoveError('');
    try { await reorderCarriers({ type: source.type, ids, expected: sameType.map(({ id, updatedAt }) => ({ id, updatedAt })) }); res.reload(); }
    catch (cause) { setMoveError(cause instanceof Error ? cause.message : '排序失败'); }
    finally { setMoving(false); }
  };
  const startSorting = () => { setDraftIds(all.map(({ id }) => id)); setMoveError(''); setSorting(true); };
  const cancelSorting = () => { dragId.current = null; setDraftIds([]); setSorting(false); setMoveError(''); };
  const finishSorting = async () => {
    if (!writable || moving || import.meta.env.VITE_READONLY !== '0') return;
    setMoving(true); setMoveError('');
    try {
      for (const carrierTypeKey of ['EXPRESS', 'LOGISTICS'] as const) {
        const original = all.filter((item) => item.type === carrierTypeKey);
        const nextIds = draftIds.filter((id) => original.some((item) => item.id === id));
        if (nextIds.length === original.length && nextIds.some((id, index) => id !== original[index]?.id)) {
          await reorderCarriers({ type: carrierTypeKey, ids: nextIds, expected: original.map(({ id, updatedAt }) => ({ id, updatedAt })) });
        }
      }
      cancelSorting();
      res.reload();
    } catch (cause) {
      setMoveError(cause instanceof Error ? cause.message : '排序保存失败');
    } finally { setMoving(false); }
  };
  const reorderDraft = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const source = all.find((item) => item.id === sourceId);
    const target = all.find((item) => item.id === targetId);
    if (!source || !target || source.type !== target.type) return;
    setDraftIds((previous) => {
      const typeIds = previous.filter((id) => all.find((item) => item.id === id)?.type === source.type);
      const from = typeIds.indexOf(sourceId); const to = typeIds.indexOf(targetId);
      if (from < 0 || to < 0) return previous;
      typeIds.splice(from, 1); typeIds.splice(to, 0, sourceId);
      let cursor = 0;
      return previous.map((id) => all.find((item) => item.id === id)?.type === source.type ? typeIds[cursor++] : id);
    });
  };
  const pointerDown = (event: ReactPointerEvent<HTMLButtonElement>, id: string) => {
    event.preventDefault(); event.stopPropagation();
    dragId.current = id;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const pointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const from = dragId.current;
    dragId.current = null;
    if (!from) return;
    const targetNode = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-carrier-id], tr[data-row-key]');
    const target = targetNode?.dataset.carrierId || targetNode?.dataset.rowKey;
    if (target) {
      if (ff === 'phone') reorderDraft(from, target);
      else void moveTo(from, target);
    }
  };
  const exportCsv = () => {
    const safe = (value: string | number) => {
      const raw = String(value);
      const escaped = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
      return `"${escaped.replaceAll('"', '""')}"`;
    };
    const lines = [['顺序', '名称', '类型', '编码', '官网', '查单地址', '近30天用量', '状态'], ...rows.map((carrier) => [order.get(carrier.id) || 0, carrier.name, carrierType[carrier.type], carrier.code || '', carrier.websiteUrl || '', carrier.trackingUrl || '', carrier.recentUse || 0, activeState(carrier.isActive).label])];
    const blob = new Blob(['\uFEFF', lines.map((line) => line.map(safe).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = '物流公司.csv'; anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const legacy = writable && ff !== 'phone' ? <span className={s.headerActions}><button type="button" onClick={() => setImporting(true)}>批量导入</button><button type="button" className={s.headerPrimary} onClick={() => setEditing('new')}>新增</button></span> : undefined;
  const mobileFooter = writable ? (sorting
    ? <><button type="button" onClick={cancelSorting} disabled={moving}>取消排序</button><button type="button" className={s.primary} onClick={() => void finishSorting()} disabled={moving}>{moving ? '保存中…' : '保存顺序'}</button></>
    : <><button type="button" onClick={() => setImporting(true)}>批量导入</button><button type="button" className={s.primary} onClick={() => setEditing('new')}>新增</button></>) : undefined;
  const mobileHeadActions = ff === 'phone' && writable ? <button type="button" className={s.sortAction} onClick={sorting ? cancelSorting : startSorting} disabled={moving}>{sorting ? '完成' : '排序'}</button> : undefined;
  const FilterControl = ff === 'tablet' ? Segmented : Chips;
  const chips = (
    <FilterControl
      value={type}
      onChange={(k) => set({ type: k === 'all' ? undefined : k })}
      items={[{ key: 'all', label: '全部', count: all.length }, { key: 'EXPRESS', label: '快递', count: nExp }, { key: 'LOGISTICS', label: '物流', count: nLog }]}
    />
  );

  const carrierHost = (url: string) => {
    try { return new URL(url).hostname.replace(/^www\./, ''); }
    catch { return url; }
  };
  const carrierCell = (c: CarrierCompany) => <Cell
    label={<><span className={`tnum ${s.idx}`}>{order.get(c.id)}</span>{c.name}{!c.isActive && <Tag tone="none" sm>停用</Tag>}</>}
    hint={[carrierType[c.type], c.websiteUrl ? carrierHost(c.websiteUrl) : c.code,].filter(Boolean).join(' · ')}
    value={<span className={s.carrierRecent}>{c.recentUse || 0} 单<Icon name="chev" size={14} /></span>}
  />;
  const cols: Col<CarrierCompany>[] = [
    { key: 'no', title: '排序', w: 44, render: (c) => writable
      ? <button type="button" className={s.carrierOrderHandle} aria-label={`拖动调整${c.name}顺序，也可用上下方向键`} disabled={moving} onPointerDown={(event) => pointerDown(event, c.id)} onPointerUp={pointerUp} onPointerCancel={() => { dragId.current = null; }} onKeyDown={(event) => { if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); void move(c, event.key === 'ArrowUp' ? -1 : 1); } }}><Icon name="grip" size={14} /></button>
      : <span className={s.carrierOrder}><Icon name="grip" size={14} /></span> },
    { key: 'name', title: '名称', w: 190, render: (c) => <b className={s.strong}>{c.name}</b> },
    { key: 'type', title: '类型', w: 110, render: (c) => carrierType[c.type] || c.type },
    { key: 'website', title: '官网 / 查单', w: 180, render: (c) => <span className={s.carrierLinks}>{c.websiteUrl ? <a href={c.websiteUrl} target="_blank" rel="noopener noreferrer">{carrierHost(c.websiteUrl)}</a> : null}{c.trackingUrl ? <a href={c.trackingUrl} target="_blank" rel="noopener noreferrer">查单</a> : null}{!c.websiteUrl && !c.trackingUrl ? <span className="em">—</span> : null}</span> },
    { key: 'use', title: '近 30 天用量', w: 118, render: (c) => <span className="tnum">{c.recentUse || 0} 单</span> },
    { key: 'st', title: '状态', w: 80, render: (c) => <Dot tone={c.isActive ? 'green' : 'grey'}>{activeState(c.isActive).label}</Dot> },
    { key: 'act', title: '操作', w: 64, align: 'r', render: (c) => writable ? <button type="button" className={s.carrierEdit} aria-label={`编辑${c.name}`} title={`编辑${c.name}`} onClick={() => setEditing(c)}>{ff === 'tablet' ? '编辑' : <Icon name="set" size={18} />}</button> : '—' },
  ];

  let body: ReactNode;
  if (res.error) body = <ErrorState title="物流公司加载失败" message={res.error.message} detail="GET /api/v2/carriers" onRetry={res.reload} />;
  else if (res.loading && !res.data) body = <Loading />;
  else if (!rows.length) body = <Empty icon="truck" title="没有物流公司">发货时要从这里选承运方。</Empty>;
  else if (ff === 'phone')
    body = (
      <Group note="序号就是发货时下拉里的顺序，点右上「排序」拖动调整。停用的公司不会出现在发货选择中，历史发货照常显示。">
        {orderedRows.map((c) => (
          <div key={c.id} data-carrier-id={c.id} className={`${s.carrierMobile} ${sorting ? s.carrierSorting : ''}`}>
            {sorting ? <><button type="button" className={s.carrierDragHandle} aria-label={`拖动调整${c.name}顺序`} onPointerDown={(event) => pointerDown(event, c.id)} onPointerUp={pointerUp} onPointerCancel={() => { dragId.current = null; }}><Icon name="grip" size={18} /></button>{carrierCell(c)}</>
              : writable ? <button type="button" className={s.carrierMobileOpen} onClick={() => setEditing(c)}>{carrierCell(c)}</button>
                : carrierCell(c)}
          </div>
        ))}
      </Group>
    );
  else
    body = (
      <div className={`${s.tableCard} ${s.carrierTableCard}`}>
        <div className={s.carrierToolbar}>{chips}{writable && <button type="button" className={s.carrierExport} onClick={exportCsv} disabled={!all.length}>导出</button>}</div>
        <DataTable cols={cols} rows={rows} rowKey={(c) => c.id} />
        <p className={s.tfoot}>共 {rows.length} 家 · 按住左侧把手拖动，调整发货下拉里的顺序</p>
      </div>
    );
  const sub = res.data ? `${ff === 'tablet' ? '发货时选择，顺序影响下拉里的排列 · ' : ''}快递 ${nExp} 家 · 物流 ${nLog} 家` : undefined;
  return (
    <Frame title="物流公司" sub={sub} legacy={ff === 'phone' ? mobileHeadActions : legacy} mobileFooter={mobileFooter}>
      {ff === 'phone' && <div className={s.chipRow}>{chips}</div>}
      {moveError && <p role="alert" className={s.moveError}>{moveError}</p>}
      {ff === 'phone' && rows.length > 0 && <p className={s.carrierListMeta}>共 {rows.length} 家 · 右侧是近 30 天用量</p>}
      {body}
      {editing && <CarrierSheet key={editing === 'new' ? 'new' : editing.id} carrier={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); res.reload(); }} />}
      {importing && <CarrierImportDialog existingCount={rows.length} onClose={() => setImporting(false)} onImported={() => res.reload()} />}
    </Frame>
  );
}

/* ================================ 店铺名称（Y4） ================================ */

export function ShopsPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const writable = can('system.shops.write');
  const [editing, setEditing] = useState<Shop | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Shop | null>(null);
  const [defaultSaving, setDefaultSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const res = useApi<ShopsPayload>('/api/v2/shops');
  const def = res.data?.defaultShopName || '';
  const rows = res.data?.items || [];
  const legacy = writable ? <span className={s.headerActions}><button type="button" className={s.headerPrimary} onClick={() => setEditing('new')}><Icon name="plus" size={16} />新增店铺</button></span> : undefined;
  const makeDefault = async (shop: Shop) => {
    if (defaultSaving || import.meta.env.VITE_READONLY !== '0') return;
    setDefaultSaving(true); setActionError('');
    try { await setDefaultShop(shop.id, def); res.reload(); }
    catch (cause) { setActionError(cause instanceof Error ? cause.message : '设置默认店铺失败'); }
    finally { setDefaultSaving(false); }
  };
  const cols: Col<Shop>[] = [
    { key: 'name', title: '店铺名称', w: 226, render: (x) => <b className={s.strong}>{x.name}</b> },
    { key: 'def', title: '默认', w: 130, render: (x) => x.name === def ? <Tag tone="green" sm>默认</Tag> : writable && x.isActive ? <button className={s.defaultAction} type="button" disabled={defaultSaving || import.meta.env.VITE_READONLY !== '0'} onClick={() => void makeDefault(x)}>设为默认</button> : '—' },
    { key: 'orders', title: '累计订单', w: 135, render: (x) => <span className="tnum">{x.orderCount || 0}</span> },
    { key: 'recent', title: '近 30 天', w: 70, render: (x) => <span className="tnum">{x.recentOrderCount || 0}</span> },
    { key: 'status', title: '状态', w: 90, render: (x) => <Dot tone={x.isActive ? 'green' : 'grey'}>{x.isActive ? '启用' : '停用'}</Dot> },
    { key: 'act', title: '操作', w: 80, align: 'r', render: (x) => writable ? <span className={s.rowActions}><button type="button" className={s.carrierEdit} aria-label={`编辑${x.name}`} title={`编辑${x.name}`} onClick={() => setEditing(x)}>{ff === 'tablet' ? '编辑' : <Icon name="set" size={18} />}</button>{!x.orderCount && <button type="button" onClick={() => setDeleting(x)}>删除</button>}</span> : '—' },
  ];
  let body: ReactNode;
  if (res.error) body = <ErrorState title="店铺加载失败" message={res.error.message} detail="GET /api/v2/shops" onRetry={res.reload} />;
  else if (res.loading && !res.data) body = <Loading />;
  else if (!rows.length) body = <Empty icon="store" title="还没有店铺">下单时必须选店铺。</Empty>;
  else if (ff === 'phone')
    body = (
      <Group note="V1 订单按店铺名称关联；有订单的店铺不能改名或删除。默认店铺在新建订单时带出。">
        {rows.map((x) => <div key={x.id} className={`${s.carrierMobile} ${s.shopMobile}`}>
          <button type="button" className={s.shopMobileOpen} onClick={() => writable && setEditing(x)} disabled={!writable} aria-label={`查看或编辑店铺 ${x.name}`}>
            <span className={s.shopMobileText}>
              <span className={s.nameCell}>{x.name}{x.name === def && <Tag tone="green" sm>默认</Tag>}</span>
              <small>累计 {x.orderCount || 0} 单 · 近 30 天 {x.recentOrderCount || 0} 单</small>
            </span>
            <Icon name="chev" size={16} />
          </button>
          {writable && x.name !== def && x.isActive && <button type="button" className={s.shopDefault} disabled={defaultSaving || import.meta.env.VITE_READONLY !== '0'} onClick={() => void makeDefault(x)}>设为默认</button>}
        </div>)}
      </Group>
    );
  else
    body = (
      <div className={`${s.tableCard} ${s.shopTableCard}`}>
        <DataTable cols={cols} rows={rows} rowKey={(x) => x.id} />
        <p className={s.tfoot}>有订单在用的店铺不能删除，只能停用</p>
      </div>
    );
  return <Frame title="店铺名称" sub={`下单时必填。用于区分销售渠道，报表按店铺分组${res.data ? ` ${rows.length} 个` : ''}`} legacy={ff === 'phone' ? undefined : legacy} mobileFooter={ff === 'phone' && writable ? <button type="button" className={s.primary} disabled={import.meta.env.VITE_READONLY !== '0'} onClick={() => setEditing('new')}><Icon name="plus" size={16} />新增店铺</button> : undefined}>
    {actionError && <p role="alert" className={s.moveError}>{actionError}</p>}
    {body}
    {editing && <ShopSheet key={editing === 'new' ? 'new' : editing.id} shop={editing === 'new' ? undefined : editing} isDefault={editing !== 'new' && editing.name === def} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); res.reload(); }} />}
    {deleting && <ShopDeleteDialog shop={deleting} isDefault={deleting.name === def} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); res.reload(); }} />}
  </Frame>;
}

/* ================================ 仓库 ================================ */

export function WarehousesPage() {
  const [editing, setEditing] = useState<Warehouse | 'new' | null>(null);
  const [action, setAction] = useState<{ row: Warehouse; kind: 'delete' | 'default' | 'up' } | null>(null);
  const user = useSession((st) => st.user);
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const res = useApi<Page<Warehouse>>('/api/warehouses', { pageSize: 500 });
  const rows = res.data?.items || [];
  const writable = can('warehouses.write');
  const legacy = writable ? <Button icon="plus" disabled={!WRITES_ENABLED} onClick={() => setEditing('new')}>新增仓库</Button> : undefined;
  const controls = (w: Warehouse) => writable && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: ff === 'phone' ? 'min(160px, 44vw)' : undefined }}>
    <Button size="sm" disabled={!WRITES_ENABLED} onClick={() => setEditing(w)}>编辑</Button>
    {!w.isDefaultInbound && <Button size="sm" disabled={!WRITES_ENABLED} onClick={() => setAction({ row: w, kind: 'default' })}>设为默认</Button>}
    {!user?.warehouseIds && rows.findIndex((row) => row.id === w.id) > 0 && <Button size="sm" disabled={!WRITES_ENABLED} onClick={() => setAction({ row: w, kind: 'up' })}>上移</Button>}
    <Button size="sm" disabled={!WRITES_ENABLED || w.isDefaultInbound} onClick={() => setAction({ row: w, kind: 'delete' })}>删除 / 停用</Button>
  </div>;
  const cols: Col<Warehouse>[] = [
    { key: 'no', title: '顺序', w: 64, render: (w) => <span className="tnum em">{w.sortOrder}</span> },
    { key: 'code', title: '仓库编码', w: 110, render: (w) => <span className="tnum">{w.code}</span> },
    { key: 'name', title: '仓库名称', w: 160, render: (w) => <b className={s.strong}>{w.name}</b> },
    { key: 'def', title: '默认入库', w: 96, render: (w) => (w.isDefaultInbound ? <Tag tone="green" sm>默认入库</Tag> : <span className="em">—</span>) },
    { key: 'addr', title: '地址', w: 320, render: (w) => w.address || <span className="em">—</span> },
    { key: 'upd', title: '更新时间', w: 120, render: (w) => <span className="tnum em">{dateTime(w.updatedAt)}</span> },
    { key: 'actions', title: '操作', w: 340, render: controls },
  ];
  let body: ReactNode;
  if (res.error) body = <ErrorState title="仓库加载失败" message={res.error.message} detail="GET /api/warehouses" onRetry={res.reload} />;
  else if (res.loading && !res.data) body = <Loading />;
  else if (!rows.length) body = <Empty icon="home" title="还没有仓库">入库、发货都要指定仓库。</Empty>;
  else if (ff === 'phone')
    body = (
      <Group note="入库时默认带出「默认入库」仓；这里只列启用中的仓库。">
        {rows.map((w) => (
          <Cell key={w.id} label={<span className={s.nameCell}>{w.name}{w.isDefaultInbound && <Tag tone="green" sm>默认入库</Tag>}</span>} hint={[w.code, w.address].filter(Boolean).join(' · ')} value={controls(w)} />
        ))}
      </Group>
    );
  else
    body = (
      <div className={s.tableCard}>
        <DataTable cols={cols} rows={rows} rowKey={(w) => w.id} dense />
        <p className={s.tfoot}>共 {rows.length} 个 · 只列启用中的仓库 · 顺序影响各处仓库下拉</p>
      </div>
    );
  return <Frame title="仓库" sub={res.data ? `${rows.length} 个仓库` : undefined} legacy={legacy}>{body}
    {editing && <WarehouseSheet warehouse={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); res.reload(); }} />}
    {action && <ConfirmWindow title={`${action.kind === 'default' ? '设为默认入库仓库' : action.kind === 'up' ? '调整仓库顺序' : '删除 / 停用仓库'} · ${action.row.name}`} disabled={!WRITES_ENABLED} onClose={() => setAction(null)} onConfirm={async () => {
      if (action.kind === 'delete') await removeWarehouse(action.row.id);
      else if (action.kind === 'default') await defaultInboundWarehouse(action.row.id);
      else { const ids = rows.map((row) => row.id); const index = ids.indexOf(action.row.id); if (index <= 0) return; [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]]; await reorderWarehouses(ids); }
      setAction(null); res.reload();
    }}><p>{action.kind === 'delete' ? '有业务数据的仓库将停用并保留历史；无业务数据的仓库会删除。默认入库仓库不能删除。' : action.kind === 'default' ? '后续入库默认带出这个仓库，已有单据保持原仓库。' : '仓库下拉列表将使用新的显示顺序。'}</p></ConfirmWindow>}
  </Frame>;
}
